import {
  HbrCheckbox,
  HbrDatePicker,
  HbrInput,
  HbrOption,
  HbrSelect,
  HbrSpinner
} from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  useFetchOpportunity,
  useFetchOpportunityProductAssignment,
  useUpdateOpportunityProductAssignmentMutation
} from '../../../../hooks/opportunity-hooks';
import {
  useCreatePoVActivity
} from '../../../../hooks/opportunity-pov-hooks';
import { setOpportunityUiValue } from '../../../../support/slices/uiSlice';
import { RootState, useAppDispatch } from '../../../../support/store';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { isPovStarted, useFetchPov } from '../../opportunityDetailHelpers';
import { OpportunityProduct, OpportunitySEStatus } from '../../../../types/opportunity';
import { useParams } from 'react-router-dom';
import PoVApprovalRequestComponent from '../../PoVApprovalRequestComponent';
import { asSingleValue } from '../../../../support/utils';
import { CacheKey } from '../../../../types/cache-keys';
import { ApprovalRequestType } from '../../../../types/approval-request';
import { queryClient } from '../../../../support/queryClient';
import CustomerTeamSection from './PoVForm/CustomerTeamSection';
import CiscoTeamSection from './PoVForm/CiscoTeamSection';
import PrerequisitesSection from './PoVForm/PrerequisitesSection';
import DatesSection from './PoVForm/DatesSection';

const style = {
  control: ['w-80', 'max-w-full', 'min-h-20'].join(' '),
  heading: ['py-4', 'text-xl', 'flex'].join(' '),
  label: ['text-[#606162]', 'font-normal', 'mb-1'].join(' '),
  input: [
    'w-full',
    'border',
    'rounded-sm',
    'px-2',
    'text-sm',
    'border-gray-400',
    'h-10',
    'text-content-primary-default',
    'px-4',
    'mb-1',
  ].join(' '),
};

const ValidatePhasePoV: React.FC<{ selectedProductId: OpportunityProduct['id'] }> = ({ selectedProductId }) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);

  const taggedOpportunities = useSelector((state: RootState) => state.salesforceOpportunities.taggedOpportunityIds);
  const { navigationViews } = useSelector((state: RootState) => state.user);
  
  const isTaggedUser = taggedOpportunities.includes(opportunityId);
  const isAdmin = navigationViews.includes('admin');

  const canUpdateOpportunity = ![
    OpportunitySEStatus.TechnicalClose,
    OpportunitySEStatus.TechnicalOnHold
  ].includes(opportunity?.SE_Status__c as OpportunitySEStatus);

  const canRequestApproval = (isTaggedUser || isAdmin) && canUpdateOpportunity;

  const { data: pov, isLoading: loadingPov } = useFetchPov(opportunity);
  const { data: selectedProduct, isLoading: loadingSelectedProduct } = useFetchOpportunityProductAssignment(selectedProductId, opportunityId, { enabled: !!opportunityId && !!selectedProductId });

  const { mutateAsync: createPoVActivity } = useCreatePoVActivity(opportunityId);

  const { name: userName } = useSelector((state: RootState) => state.user.userData);
  const { name: managerName } = useSelector((state: RootState) => state.user.manager);
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadingCisco, setLoadingCisco] = useState(true);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingPrereqs, setLoadingPrereqs] = useState(true);

  useEffect(() => {
    setIsLoading(loadingPov || loadingTeam || loadingCisco || loadingDates || loadingPrereqs || loadingSelectedProduct);
  }, [ loadingPov, loadingTeam, loadingCisco, loadingDates, loadingPrereqs, loadingSelectedProduct ]);

  const isFormDirtyRef = useRef({
    opportunityProduct: false,
  });
  const [isFormUpdated, setIsFormUpdated] = useState({
    opportunityProduct: false,
  });

  const { mutateAsync: updateOpportunityProduct, isPending: isUpdatingOpportunityProduct } = useUpdateOpportunityProductAssignmentMutation();

  const formUpdateComplete = useMemo(() =>
    !isUpdatingOpportunityProduct,
    [isUpdatingOpportunityProduct]);
  useEffect(() => {
    if (formUpdateComplete) {
      queryClient.invalidateQueries({ queryKey: [
        CacheKey.ApprovalRequestDetails,
        ApprovalRequestType.PoV, 
        pov?.id,
        opportunityId,
        selectedProduct?.id
      ].map(String) });
    }
  }, [formUpdateComplete, pov?.id, opportunityId, selectedProduct?.id]);

  const [startingPoV, setStartingPoV] = useState(false);
  const [teamValid, setTeamValid] = useState(false);
  const [ciscoTeamValid, setCiscoTeamValid] = useState(false);
  const [datesValid, setDatesValid] = useState(false);

  const [canStartPov, setCanStartPov] = useState(false);

  const validateStartPov = () => {
    setCanStartPov(
      !!(
        teamValid &&
        ciscoTeamValid &&
        datesValid
      )
    );
  };

  // Trigger validation when teamValid, ciscoTeamValid, or datesValid changes
  useEffect(() => {
    validateStartPov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamValid, ciscoTeamValid, datesValid]);

  const handleSeManagerApprovedChange = (approved: boolean) => {
    if (selectedProduct && approved !== selectedProduct.sem_approved) {
      updateOpportunityProduct({
        ...selectedProduct,
        product_id: selectedProduct?.product?.id,
        sem_approved: approved,
      });
    }
  };

  const showAddCustomerForm = () => {
    dispatch(
      setOpportunityUiValue({
        property: 'showOpportunityDrawer',
        value: true,
      })
    );
    dispatch(
      setOpportunityUiValue({
        property: 'showCustomerForm',
        value: true,
      })
    );
  };

  const checkPlannedDatesSet = () => {
    if (!selectedProduct) return;
    const startDateNotSet = selectedProduct.planned_start_date === '1999-01-01';
    const endDateNotSet = selectedProduct.planned_end_date === '1999-01-01';

    if (startDateNotSet || endDateNotSet) {
      const formatter = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });

      updateOpportunityProduct({
        ...selectedProduct,
        product_id: selectedProduct?.product?.id,
        planned_start_date: startDateNotSet ? formatter.format(new Date()) : selectedProduct.planned_start_date,
        planned_end_date: endDateNotSet ? formatter.format(new Date()) : selectedProduct.planned_end_date,
      });
    }
  };





  const startPoV = async () => {
    setStartingPoV(true);
    const formatter = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const taskResponse = await createPoVActivity(selectedProductId);

    if (taskResponse.error) {
      console.error(taskResponse.error);
    }

    // Always update pov_kickoff_date even if task creation fails
    // If task creation succeeded, also include sf_pov_activity_id and sf_pov_activity_created
    await updateOpportunityProduct({
      ...selectedProduct,
      product_id: selectedProduct.product.id,
      pov_kickoff_date: formatter.format(new Date()),
      ...(taskResponse && !taskResponse.error && {
        sf_pov_activity_id: taskResponse.taskResponse?.id,
        sf_pov_activity_created: taskResponse.taskResponse?.success,
      }),
    });
    setStartingPoV(false);
  };

  return (
    <>
      {(!selectedProduct) && <HbrSpinner size='medium' />}
      {selectedProduct && (
        <>
          <div className='grid grid-cols-1 lg:grid-cols-2 min-w-60'>
            <div className='flex justify-between w-full lg:col-span-2 mb-6 items-center'>
              <div className={`${style.heading}`}>Customer Team</div>
              {!isPovStarted(selectedProduct) && (
                <StbButton disabled={!canStartPov} onClick={startPoV} loading={startingPoV} role="request">
                  Start PoV
                </StbButton>
              )}
              {isPovStarted(selectedProduct) && (
                <div className="border border-gray-300 flex-col justify-items-center item-center h-full text-gray-700 p-2 rounded-md">
                  <div className="text-xs font-semibold">Pov started</div>
                  <div>{selectedProduct.pov_kickoff_date}</div>
                </div>
              )}
            </div>

            <div className='relative lg:mr-4'>
              <CustomerTeamSection
                pov={pov}
                selectedProductId={selectedProductId}
                onValidationChange={setTeamValid}
                onLoadingChange={setLoadingTeam}
                showAddCustomerForm={showAddCustomerForm}
              />

              <div className='my-6'>
                <div className={`${style.heading}`}>Cisco Team</div>
              </div>
              <CiscoTeamSection
                pov={pov}
                selectedProduct={selectedProduct}
                onValidationChange={setCiscoTeamValid}
                onLoadingChange={setLoadingCisco}
                onSeManagerApprovedChange={handleSeManagerApprovedChange}
              />
            </div>
            <div className='relative lg:mr-4'>
              <DatesSection
                pov={pov}
                selectedProductId={selectedProductId}
                onValidationChange={setDatesValid}
                onLoadingChange={setLoadingDates}
              />

              <PrerequisitesSection
                pov={pov}
                selectedProductId={selectedProductId}
                onValidationChange={() => {}} // Prerequisites validation is optional
                onLoadingChange={setLoadingPrereqs}
              />
            </div>
          </div>

          <PoVApprovalRequestComponent
            requestTitle={`PoV Approval Request - ${selectedProduct?.product.name} - ${opportunity?.Name}`}
            requestActionsEnabled={canRequestApproval}
            selectedProduct={selectedProduct.id}
            onRequestOpen={async () => {
              checkPlannedDatesSet();
            }}
          />
        </>
      )}
    </>
  );
};

export default ValidatePhasePoV;
